import { createAsyncThunk, createSlice, isAnyOf, PayloadAction } from "@reduxjs/toolkit";
import uniqBy from "lodash/uniqBy";
import { REQUEST_LOADING_STATES } from "../../helpers/constant";
import { ICollectionsState, IDeleteResponse } from "../../helpers/types";
import { collectionService } from "../../services/collection.service";

const initialState: ICollectionsState = {
  userCollections: null,
  otherUserCollections: [],
  loading: REQUEST_LOADING_STATES.IDLE,
  error: null,
  shouldNavigateToPublicationPage: false,
  publicCollections: null,
};

export const getUserCollections = createAsyncThunk("collections/getUserCollections", async (page: number) => {
  const data = await collectionService.getCreatorCollections(page);
  return { ...data, page };
});

export const getOtherUserCollections = createAsyncThunk(
  "collections/getOtherUserCollections",
  async ({ creatorId, page }: { creatorId: string; page: number }) => {
    const data = await collectionService.getFanCollections(creatorId, page);
    return { ...data, page };
  }
);

export const getPublicCollections = createAsyncThunk(
  "collections/getPublicCollections",
  async ({ creatorId, page }: { creatorId: string; page: number }) => {
    const data = await collectionService.getPublicCollections(creatorId, page);
    return { ...data, page };
  }
);

export const deleteCollectionById = createAsyncThunk<IDeleteResponse, string>("collections/deleteCollection", async (collectionId: string) => {
  return await collectionService.deleteCollection(collectionId);
});

const collectionsSlice = createSlice({
  name: "collections",
  initialState,
  reducers: {
    clearCollectionsCache: (state, action: PayloadAction<{ creatorId?: string; isMyProfile?: boolean }>) => {
      const { creatorId, isMyProfile } = action.payload;
      if (isMyProfile) {
        state.userCollections = null;
      } else if (creatorId) {
        state.otherUserCollections = state.otherUserCollections.filter((u) => u.id !== creatorId);
        state.publicCollections = null;
      } else {
        state.userCollections = null;
        state.otherUserCollections = [];
        state.publicCollections = null;
      }
      state.shouldNavigateToPublicationPage = false;
    },
    updateCollectionsLocally: (state, action: { payload: { creatorId?: string; isMyProfile?: boolean; collections: any[] } }) => {
      const { creatorId, isMyProfile, collections } = action.payload;
      if (isMyProfile && state.userCollections) {
        state.userCollections.collections = collections;
      } else if (creatorId) {
        const idx = state.otherUserCollections.findIndex((u) => u.id === creatorId);
        if (idx !== -1) {
          state.otherUserCollections[idx].collections = collections;
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getOtherUserCollections.fulfilled, (state, action) => {
      state.loading = REQUEST_LOADING_STATES.SUCCEEDED;
      const collections = action.payload.data;
      const total = action?.payload?.total;

      const otherUserCollections = state.otherUserCollections;
      const user = otherUserCollections.findIndex((user) => user.id === collections[0].creatorId);
      if (user !== -1) {
        const currentPage = otherUserCollections[user].page;
        const merged = uniqBy([...otherUserCollections[user].collections, ...collections], "_id");
        const hasMore = merged.length < total;
        otherUserCollections[user] = {
          ...otherUserCollections[user],
          collections: merged,
          ttl: 5,
          lft: new Date().getTime(),
          hasMore,
          total,
          page: hasMore ? currentPage + 1 : currentPage,
        };
        state.otherUserCollections = otherUserCollections;
      } else {
        const currentPage = action.payload.page;
        if (currentPage === 1 && collections.length === 0) {
          state.shouldNavigateToPublicationPage = true;
        } else {
          const hasMore = collections.length < total;
          state.otherUserCollections = [
            ...otherUserCollections,
            {
              id: collections[0].creatorId,
              collections: collections,
              ttl: 5,
              lft: new Date().getTime(),
              hasMore,
              total,
              page: hasMore ? currentPage + 1 : currentPage,
            },
          ];
        }
      }
    });

    builder.addCase(getUserCollections.fulfilled, (state, action) => {
      state.loading = REQUEST_LOADING_STATES.SUCCEEDED;
      const currentCollections = state.userCollections;
      const collections = action.payload.data;
      const total = action?.payload?.total;

      if (currentCollections) {
        const currentPage = currentCollections.page;
        const merged = uniqBy([...currentCollections.collections, ...collections], "_id");
        const hasMore = merged.length < total;
        state.userCollections = {
          ...currentCollections,
          collections: merged,
          ttl: 5,
          lft: new Date().getTime(),
          page: hasMore ? currentPage + 1 : currentPage,
          hasMore,
          total,
        };
      } else {
        const currentPage = action.payload.page;
        if (currentPage === 1 && collections.length === 0) {
          state.shouldNavigateToPublicationPage = true;
        } else {
          const hasMore = collections.length < total;
          state.userCollections = {
            id: "",
            collections: collections,
            hasMore,
            ttl: 5,
            lft: new Date().getTime(),
            page: hasMore ? currentPage + 1 : currentPage,
            total,
          };
        }
      }
    });

    builder.addCase(getPublicCollections.fulfilled, (state, action) => {
      state.loading = REQUEST_LOADING_STATES.SUCCEEDED;
      const publicCollections = state.publicCollections;
      const collections = action.payload.data;
      const total = action?.payload?.total;

      if (publicCollections) {
        const currentPage = publicCollections.page;
        const merged = uniqBy([...publicCollections.collections, ...collections], "_id");
        const hasMore = merged.length < total;
        state.publicCollections = {
          ...publicCollections,
          collections: merged,
          ttl: 5,
          lft: new Date().getTime(),
          page: hasMore ? currentPage + 1 : currentPage,
          hasMore,
          total,
        };
      } else {
        const currentPage = action.payload.page;
        if (currentPage === 1 && collections.length === 0) {
          state.shouldNavigateToPublicationPage = true;
        } else {
          const hasMore = collections.length < total;
          state.publicCollections = {
            id: "",
            collections: collections,
            hasMore,
            ttl: 5,
            lft: new Date().getTime(),
            page: hasMore ? currentPage + 1 : currentPage,
            total,
          };
        }
      }
    });

    builder.addCase(deleteCollectionById.fulfilled, (state, action) => {
      const deletedId = action.meta.arg;
      if (state.userCollections) {
        state.userCollections.collections = state.userCollections.collections.filter((c) => c._id !== deletedId);
      }
      state.otherUserCollections = state.otherUserCollections.map((u) => ({
        ...u,
        collections: u.collections.filter((c) => c._id !== deletedId),
      }));
      if (state.publicCollections) {
        state.publicCollections.collections = state.publicCollections.collections.filter((c) => c._id !== deletedId);
      }
    });

    builder.addMatcher(isAnyOf(getUserCollections.pending, getOtherUserCollections.pending, getPublicCollections.pending), (state) => {
      state.loading = REQUEST_LOADING_STATES.PENDING;
      state.error = null;
    });

    builder.addMatcher(isAnyOf(getOtherUserCollections.rejected, getUserCollections.rejected, getPublicCollections.rejected), (state, action) => {
      state.loading = REQUEST_LOADING_STATES.FAILED;
      state.error = action.error.message || "Something went wrong";
    });
  },
});

export const { clearCollectionsCache, updateCollectionsLocally } = collectionsSlice.actions;
export default collectionsSlice.reducer;
